import * as T from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { clone } from 'three/addons/utils/SkeletonUtils.js';
import { SKINS, OUTFITS, type Character } from './game';
import { decodeCharacterData } from './character-data';

export type Motion = 'idle' | 'walk' | 'run' | 'wave' | 'interact';
// what someone in a place is doing when they are not walking: the clips cover standing and gestures, and the
// rest (sitting, dancing, cheering) is posed on top of them by turning the bones
export type Pose = 'stand' | 'talk' | 'sit' | 'dance' | 'workout' | 'walk' | 'cheer' | 'work';
const clipNames: Record<Motion, string> = {
  idle: 'Idle_Neutral',
  walk: 'Walk',
  run: 'Run',
  wave: 'Wave',
  interact: 'Interact',
};
// scratch values for posing (one at a time, so shared)
const AIM = {
  start: new T.Vector3(),
  tip: new T.Vector3(),
  want: new T.Vector3(),
  v: new T.Vector3(),
  facing: new T.Quaternion(),
  turn: new T.Quaternion(),
  world: new T.Quaternion(),
  parent: new T.Quaternion(),
};
// Three immutable sources at most. Instances share geometry, never bones.
const sources = new Map<string, Promise<GLTF>>();
export function characterAssetKey(character: Character): 'male' | 'female' | 'mandem' {
  return character.body === 'female' ? 'female' : character.look === 'mandem' ? 'mandem' : 'male';
}
function loadSource(character: Character) {
  const key = characterAssetKey(character);
  let source = sources.get(key);
  if (!source) {
    const asset =
      key === 'female'
        ? import('./character-assets/female')
        : key === 'mandem'
          ? import('./character-assets/mandem')
          : import('./character-assets/male');
    source = asset
      .then(({ default: data }) => new GLTFLoader().parseAsync(decodeCharacterData(data), ''))
      .catch((error) => {
        sources.delete(key); // A failed application chunk can be retried.
        throw error;
      });
    sources.set(key, source);
  }
  return source;
}

export class AnimatedCharacter {
  readonly group = new T.Group();
  readonly ready: Promise<void>;
  private root?: T.Object3D;
  private mixer?: T.AnimationMixer;
  private actions = new Map<Motion, T.AnimationAction>();
  private motion: Motion = 'idle';
  private speed = 1;
  private disposed = false;
  private materials = new Set<T.Material>();
  private accessoryGeometry = new Set<T.BufferGeometry>();
  private skeletons = new Set<T.Skeleton>();
  private bones = new Map<string, T.Bone>();
  private pose: Pose = 'stand';
  // the seat's height in metres (sitting), and how long the pose has run (the dance's beat)
  private seat = 0.45;
  private clock = Math.random() * 10;
  private gesture = 2 + Math.random() * 4;
  private baseY = 0;

  constructor(character: Character, source?: GLTF) {
    this.ready = (source ? Promise.resolve(source) : loadSource(character)).then((gltf) => {
      if (!this.disposed) this.install(gltf, character);
    });
  }

  private install(source: GLTF, character: Character) {
    const root = clone(source.scene);
    this.root = root;
    const female = character.body === 'female';
    const mandem = !female && character.look === 'mandem';
    const hairColor = character.hairColour ?? (character.skin < 2 ? '#573d30' : '#231e22');
    const skinColor = character.skinColour ?? SKINS[character.skin];
    const outfitColor = character.outfitColour ?? OUTFITS[character.outfit];
    let head: T.Object3D | undefined;
    let headSkin: T.Mesh | undefined;
    root.traverse((object) => {
      if (object.name === 'Head') head = object;
      if (!(object instanceof T.Mesh)) return;
      object.castShadow = true;
      object.receiveShadow = true;
      object.frustumCulled = false;
      if (object instanceof T.SkinnedMesh) this.skeletons.add(object.skeleton);
      const section = `${object.name}/${object.parent?.name ?? ''}`;
      const original = Array.isArray(object.material) ? object.material : [object.material];
      const materials = original.map((material) => {
        const own = material.clone() as T.MeshStandardMaterial;
        own.roughness = 0.8;
        own.metalness = 0;
        this.materials.add(own);
        if (/^Skin/.test(own.name)) {
          own.color.set(skinColor);
          if (own.name === 'Skin_Darker') own.color.multiplyScalar(0.82);
          if (/Head/.test(section) && own.name === 'Skin') headSkin = object;
        } else if (/Body/.test(section)) {
          own.color.set(mandem && own.name === 'White' ? '#f3eee6' : outfitColor);
        } else if (/Legs/.test(section)) {
          own.color.set(mandem ? outfitColor : '#293b4b');
          if (mandem) own.color.multiplyScalar(0.88);
          own.vertexColors = false;
        } else if (/Feet/.test(section)) {
          own.color.set(own.name === 'White' ? '#ede8df' : '#313a3e');
        } else if (/Hair|Eyebrows/.test(own.name)) {
          own.color.set(hairColor);
        }
        return own;
      });
      object.material = Array.isArray(object.material) ? materials : materials[0];
      const name = materials[0].name;
      const isHair = name === (female ? 'Hair_Blond' : 'Hair');
      // Keep the original short male cut / female bob. Other hair follows Head.
      if (isHair && !(female ? character.hair === 2 : character.hair === 0)) object.visible = false;
    });
    this.group.add(root);
    // build: slim, average or broad, by widening the body a little (the avatar kit uses real shape morphs)
    const width = character.buildScale ?? 1;
    root.scale.set(root.scale.x * width, root.scale.y, root.scale.z * width);
    this.mixer = new T.AnimationMixer(root);
    for (const [motion, name] of Object.entries(clipNames) as [Motion, string][]) {
      const clip = source.animations.find((animation) => animation.name === name);
      if (!clip) throw new Error(`Character animation ${name} is missing.`);
      const action = this.mixer.clipAction(clip);
      if (motion === 'wave' || motion === 'interact') {
        action.setLoop(T.LoopOnce, 1);
        action.clampWhenFinished = true;
      }
      this.actions.set(motion, action);
    }
    // Establish an idle pose before measuring the human, not the export pose.
    this.actions.get('idle')!.play();
    this.mixer.update(0);
    root.updateMatrixWorld(true);
    if (mandem && head && headSkin) this.addMask(head, headSkin, root);
    if (head && headSkin && !(female ? character.hair === 2 : character.hair === 0)) {
      this.addHair(head, headSkin, character.hair, hairColor, root);
    }
    const box = new T.Box3().setFromObject(root, true);
    const scale = 2.4 / (box.max.y - box.min.y);
    root.scale.multiplyScalar(scale);
    root.position.y = -box.min.y * scale;
    this.baseY = root.position.y;
    root.traverse((object) => {
      if (object instanceof T.Bone) this.bones.set(object.name, object);
    });
    this.mixer.addEventListener('finished', (event) => {
      if (event.action === this.actions.get(this.motion)) this.setMotion('idle');
    });
    const requested = this.motion;
    this.motion = 'idle';
    this.setMotion(requested, this.speed);
  }

  private addMask(head: T.Object3D, face: T.Mesh, root: T.Object3D) {
    const box = new T.Box3().setFromObject(face, true);
    const width = box.max.x - box.min.x,
      height = box.max.y - box.min.y;
    const centerX = (box.min.x + box.max.x) / 2;
    const mask = new T.Group();
    mask.name = 'MandemMask';
    const fabric = new T.MeshStandardMaterial({ color: '#141619', roughness: 0.97, side: T.DoubleSide });
    fabric.name = 'Mask_Fabric';
    this.materials.add(fabric);
    const positions: number[] = [],
      indices: number[] = [];
    const columns = 12,
      rows = 4;
    // Wrap a small cloth shell around the cheeks, below the eyes and over the nose.
    const point = (column: number, row: number) => {
      const angle = ((column / columns) * 2 - 1) * 1.22;
      const down = row / rows,
        edge = Math.abs(Math.sin(angle));
      const top = box.min.y + height * (0.54 - 0.05 * edge);
      const bottom = box.min.y + height * (0.14 + 0.08 * edge);
      return new T.Vector3(
        centerX + width * 0.52 * Math.sin(angle) * (1 - 0.14 * down),
        T.MathUtils.lerp(top, bottom, down),
        box.max.z + width * 0.045 - width * (0.41 * (1 - Math.cos(angle)) + 0.1 * down),
      );
    };
    for (let row = 0; row <= rows; row++)
      for (let column = 0; column <= columns; column++) {
        positions.push(...point(column, row).toArray());
        if (row < rows && column < columns) {
          const i = row * (columns + 1) + column;
          indices.push(i, i + columns + 1, i + 1, i + 1, i + columns + 1, i + columns + 2);
        }
      }
    const geometry = new T.BufferGeometry();
    geometry.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    this.accessoryGeometry.add(geometry);
    const cover = new T.Mesh(geometry, fabric);
    cover.name = 'FaceMask';
    mask.add(cover);
    for (const side of [-1, 1]) {
      const column = side < 0 ? 0 : columns;
      const x = centerX + side * width * 0.53;
      const path = new T.CatmullRomCurve3([
        point(column, 0),
        new T.Vector3(x, box.min.y + height * 0.49, box.max.z - width * 0.4),
        new T.Vector3(x, box.min.y + height * 0.33, box.max.z - width * 0.68),
        new T.Vector3(x, box.min.y + height * 0.2, box.max.z - width * 0.4),
        point(column, rows),
      ]);
      const loopGeometry = new T.TubeGeometry(path, 12, width * 0.012, 3, false);
      this.accessoryGeometry.add(loopGeometry);
      const loop = new T.Mesh(loopGeometry, fabric);
      loop.name = 'MaskEarLoop';
      mask.add(loop);
    }
    mask.traverse((object) => {
      if (object instanceof T.Mesh) {
        object.castShadow = true;
        object.receiveShadow = true;
        object.frustumCulled = false;
      }
    });
    root.add(mask);
    root.updateMatrixWorld(true);
    head.attach(mask); // Keep the fit while following every animated head pose.
  }

  private addHair(head: T.Object3D, face: T.Mesh, style: number, color: string, root: T.Object3D) {
    const box = new T.Box3().setFromObject(face, true);
    const width = box.max.x - box.min.x;
    const center = box.getCenter(new T.Vector3());
    const hair = new T.Group();
    hair.position.set(center.x, box.max.y - 0.018, center.z - 0.025);
    const material = new T.MeshStandardMaterial({ color, roughness: 0.95 });
    this.materials.add(material);
    const ball = (radius: number, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1) => {
      const geometry = new T.IcosahedronGeometry(radius, 1);
      this.accessoryGeometry.add(geometry);
      const mesh = new T.Mesh(geometry, material);
      mesh.position.set(x, y, z);
      mesh.scale.set(sx, sy, sz);
      mesh.castShadow = true;
      hair.add(mesh);
    };
    if (style === 1) {
      // Small coils above the scalp, keeping the source face proportions.
      for (let ring = 0; ring < 2; ring++)
        for (let i = 0; i < 9; i++) {
          const a = (i * Math.PI * 2) / 9;
          const r = width * (ring ? 0.42 : 0.23);
          ball(width * 0.19, Math.cos(a) * r, ring ? 0.018 : 0.055, Math.sin(a) * r);
        }
      ball(width * 0.22, 0, 0.058, 0);
    } else {
      ball(width * 0.56, 0, -0.013, -0.004, 1, 0.45, 1.07);
      if (style === 2) {
        ball(width * 0.3, -width * 0.43, -0.125, -0.02, 0.55, 2, 1.4);
        ball(width * 0.3, width * 0.43, -0.125, -0.02, 0.55, 2, 1.4);
        ball(width * 0.47, 0, -0.11, -width * 0.45, 1, 1.25, 0.3);
      }
      if (style === 3) ball(width * 0.31, 0, 0.035, -width * 0.5);
    }
    root.add(hair);
    root.updateMatrixWorld(true);
    head.attach(hair); // Preserve world placement while inheriting the head pose.
  }

  setMotion(motion: Motion, speed = 1) {
    this.speed = speed;
    if (motion === this.motion) {
      this.actions.get(motion)?.setEffectiveTimeScale(speed);
      return;
    }
    const previous = this.actions.get(this.motion);
    this.motion = motion;
    const next = this.actions.get(motion);
    if (!next) return; // The requested state is applied when loading completes.
    previous?.fadeOut(0.22);
    next.reset().setEffectiveTimeScale(speed).setEffectiveWeight(1).fadeIn(0.22).play();
  }

  // hold a pose (people in a place); 'walk' and 'stand' are left to the clips
  setPose(pose: Pose, seat = 0.45) {
    this.pose = pose;
    this.seat = seat;
    if (this.root) this.root.position.y = this.baseY;
    if (this.root) this.root.rotation.y = 0;
    const clip: Record<Pose, Motion> = {
      stand: 'idle',
      talk: 'idle',
      sit: 'idle',
      dance: 'idle',
      cheer: 'idle',
      workout: 'run',
      walk: 'walk',
      work: 'interact',
    };
    this.setMotion(clip[pose], pose === 'workout' ? 0.8 : 1);
  }

  update(seconds: number) {
    this.mixer?.update(seconds);
    if (!this.root || !this.bones.size) return;
    this.clock += seconds;
    switch (this.pose) {
      case 'sit':
        this.sit();
        break;
      case 'dance':
      case 'cheer':
        this.dance(this.pose === 'cheer');
        break;
      case 'talk':
      case 'work':
        // a gesture now and then while talking; working repeats its move
        if ((this.gesture -= seconds) <= 0) {
          this.gesture = this.pose === 'work' ? 3.2 : 3 + Math.random() * 5;
          this.setMotion(this.pose === 'work' || Math.random() < 0.6 ? 'interact' : 'wave');
        }
        break;
    }
  }

  // turn a bone so it points (from its root to its tip, along its +y) in a direction given in the
  // character's own space (+z is forward, +y up)
  private aim(name: string, direction: T.Vector3) {
    const bone = this.bones.get(name);
    if (!bone?.parent) return;
    bone.updateWorldMatrix(true, false);
    const start = bone.getWorldPosition(AIM.start);
    const now = bone
      .localToWorld(AIM.tip.set(0, 1, 0))
      .sub(start)
      .normalize();
    const wanted = AIM.want.copy(direction).applyQuaternion(this.group.getWorldQuaternion(AIM.facing)).normalize();
    const turn = AIM.turn.setFromUnitVectors(now, wanted);
    const world = bone.getWorldQuaternion(AIM.world).premultiply(turn);
    const parent = bone.parent.getWorldQuaternion(AIM.parent).invert();
    bone.quaternion.copy(parent.multiply(world));
    bone.updateWorldMatrix(false, true);
  }

  private sit() {
    const root = this.root!;
    // thighs out in front, shins down
    for (const side of ['L', 'R']) {
      this.aim(`UpperLeg${side}`, AIM.v.set(side === 'L' ? 0.08 : -0.08, -0.08, 1));
      this.aim(`LowerLeg${side}`, AIM.v.set(0, -1, 0.12));
    }
    // sink so the hips rest on the seat
    const hip = this.bones.get('UpperLegL');
    if (hip) {
      root.updateWorldMatrix(true, true);
      const y = hip.getWorldPosition(AIM.start).y;
      const scale = this.group.getWorldScale(AIM.tip).y || 1;
      root.position.y += (this.seat + 0.08 - y) / scale;
      root.updateWorldMatrix(false, true);
    }
    // the feet follow the shins (they are separate targets in this rig)
    for (const side of ['L', 'R']) {
      const shin = this.bones.get(`LowerLeg${side}`),
        foot = this.bones.get(`Foot${side}`);
      if (!shin || !foot?.parent) continue;
      const knee = shin.getWorldPosition(AIM.start);
      const length = this.bones.get(`UpperLeg${side}`)?.getWorldPosition(AIM.tip).distanceTo(knee) ?? 0.5;
      const ankle = AIM.want.set(0, -1, 0.12).applyQuaternion(this.group.getWorldQuaternion(AIM.facing)).normalize();
      const at = AIM.v.copy(knee).addScaledVector(ankle, length * 0.95);
      foot.position.copy(foot.parent.worldToLocal(at));
    }
  }

  private dance(cheer: boolean) {
    const root = this.root!;
    const beat = this.clock * (cheer ? 5 : 4.2);
    const scale = this.group.getWorldScale(AIM.tip).y || 1;
    root.position.y = this.baseY + (Math.abs(Math.sin(beat)) * (cheer ? 0.09 : 0.05)) / scale;
    root.rotation.y = cheer ? 0 : Math.sin(beat / 2) * 0.35;
    for (const side of ['L', 'R']) {
      const out = side === 'L' ? 1 : -1;
      const lift = cheer ? 1 : 0.55 + Math.sin(beat + (out > 0 ? 0 : Math.PI)) * 0.45;
      this.aim(`UpperArm${side}`, AIM.v.set(out * (cheer ? 0.3 : 0.7), lift, cheer ? 0.15 : 0.35));
    }
  }

  dispose() {
    this.disposed = true;
    this.mixer?.stopAllAction();
    if (this.root) this.mixer?.uncacheRoot(this.root);
    this.materials.forEach((material) => material.dispose());
    this.accessoryGeometry.forEach((geometry) => geometry.dispose());
    this.skeletons.forEach((skeleton) => skeleton.dispose());
    this.group.clear();
    this.actions.clear();
  }
}
